import axios from 'axios';
import { DateTime } from 'luxon';

export const sanitizeHTML = html => {
  if (!html) return '';

  // Check if the message contains a full HTML document
  const isFullHTML = /<!DOCTYPE html>/i.test(html);

  if (isFullHTML) {
    // Extract body content only
    const bodyMatch = html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
    return bodyMatch ? bodyMatch[1] : 'Invalid Message Format';
  }

  return html.replace(/&nbsp;/g, ' '); // Clean up spaces in normal HTML messages
};

export const formatNotificationTime = timestamp => {
  let date = new Date(timestamp);

  let hours = date.getHours();
  let minutes = date.getMinutes();

  let year = date.getFullYear();
  let month = date.getMonth() + 1; // Month is 0-indexed
  let day = date.getDate();

  // Format date to MM/DD/YYYY
  month = month < 10 ? '0' + month : month;
  day = day < 10 ? '0' + day : day;

  let ampm = hours >= 12 ? 'PM' : 'AM';

  hours = hours % 12;
  hours = hours ? hours : 12;

  minutes = minutes < 10 ? '0' + minutes : minutes;

  return `${month}-${day}-${year} ${hours}:${minutes} ${ampm}`;
};

const monthNames = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec'
];
export const formatDateUTCRangeFilter = date => {
  if (!date) return '';
  const localDate = new Date(date);
  const utcDate = new Date(
    Date.UTC(localDate.getFullYear(), localDate.getMonth(), localDate.getDate())
  );
  const day = utcDate.getUTCDate().toString().padStart(2, '0');
  const month = (utcDate.getUTCMonth() + 1).toString().padStart(2, '0');
  const year = utcDate.getUTCFullYear();
  return `${year}-${month}-${day}`;
};
export const formatDateUTC = date => {
  if (!date) return '';
  const localDate = new Date(date);
  const utcDate = new Date(
    Date.UTC(localDate.getFullYear(), localDate.getMonth(), localDate.getDate())
  );
  const day = utcDate.getUTCDate().toString().padStart(2, '0');
  const month = monthNames[utcDate.getUTCMonth()];
  const year = utcDate.getUTCFullYear();

  return `${month} ${day}, ${year}`;
};

export const formatDateUTCFormat = date => {
  if (!date) return '';
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  console.log('User Time Zone:', userTimeZone);

  // Convert PickupDate to user's time zone
  const pickupDate = DateTime.fromISO(date, {
    zone: userTimeZone
  }).startOf('day');
  const localDate = new Date(pickupDate);
  const utcDate = new Date(
    Date.UTC(localDate.getFullYear(), localDate.getMonth(), localDate.getDate())
  );
  const day = utcDate.getUTCDate().toString().padStart(2, '0');
  const month = monthNames[utcDate.getUTCMonth()];
  const year = utcDate.getUTCFullYear();

  return `${month} ${day}, ${year}`;
};

export const deformatDateUTC = dDate => {
  const [date, year] = dDate.split(',');
  const month = date.slice(0, 3);
  const day = date.slice(3);

  const montth = (monthNames.indexOf(month) + 1).toString();

  return `${year.trim()}-${montth.padStart(2, 0)}-${day.trim()}`;
};

export function cleanHtmlString(html) {
  // Create a temporary div to decode HTML entities like &nbsp;
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  const decoded = tempDiv.textContent || tempDiv.innerText || '';

  // Remove all HTML tags using regex
  const cleaned = decoded.replace(/<\/?[^>]+(>|$)/g, '').trim();

  return cleaned;
}

export const getOrdinal = n => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

export const formatNumberWithCommas = value => {
  if (value === null || value === undefined || value === '') return '';

  // Remove existing commas if any, and try parsing the number
  const numericValue = Number(String(value).replace(/,/g, ''));

  if (isNaN(numericValue)) return '';

  return numericValue.toLocaleString('en-US');
};

export function formatPhoneNumber(value) {
  const cleaned = value?.replace(/\D/g, ''); // keep only digits
  const match = cleaned?.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);

  if (!match) return value;

  let formatted = '';
  if (match[1]) formatted = `(${match[1]}`;
  if (match[2]) formatted += `) ${match[2]}`;
  if (match[3]) formatted += `-${match[3]}`;
  return formatted;
}

const GOOGLE_API_KEY = 'AIzaSyBZUlA6YY8998nbFVsE_VuXZ4JpQRJwgHY';

export const getCoordinatesByAddress = async address => {
  if (!address) {
    return [];
  }
  try {
    const response = await axios.get(
      `https://maps.googleapis.com/maps/api/geocode/json`,
      {
        params: {
          address: address,
          key: GOOGLE_API_KEY,
          components: `postal_code:${address}|country:US|country:CA|country:MX`
        }
      }
    );

    if (response.data.status === 'OK') {
      const filteredResult = response.data.results.find(result => {
        const addressComponents = result.address_components;

        const postalCode = addressComponents
          .find(component => component.types.includes('postal_code'))
          ?.short_name?.replace(/\s/g, ''); // Remove spaces in postal code

        const city = addressComponents.find(component =>
          component.types.includes('locality')
        )?.short_name;

        const state = addressComponents.find(component =>
          component.types.includes('administrative_area_level_1')
        )?.short_name;

        const country = addressComponents.find(component =>
          component.types.includes('country')
        )?.short_name;

        return city && state && postalCode && country; // Ensure valid match
      });

      if (filteredResult) {
        const postalCode = filteredResult.address_components
          .find(component => component.types.includes('postal_code'))
          ?.short_name?.replace(/\s/g, '');

        const state = filteredResult.address_components.find(component =>
          component.types.includes('administrative_area_level_1')
        )?.short_name;

        let country = filteredResult.address_components.find(component =>
          component.types.includes('country')
        )?.short_name;
        if (country === 'CA') country = 'CAN';
        if (country === 'US') country = 'USA';

        // Extract postcode_localities if available
        const postcodeLocalities = filteredResult.postcode_localities || [];

        let finalAddressArray = [];

        if (postcodeLocalities.length > 0) {
          // Multiple cities exist for the ZIP code
          finalAddressArray = postcodeLocalities.map(
            city => `${city} ${state} ${postalCode} ${country}`
          );
        } else {
          // Single city case
          const city = filteredResult.address_components.find(component =>
            component.types.includes('locality')
          )?.short_name;

          finalAddressArray = [`${city} ${state} ${postalCode} ${country}`];
        }

        return finalAddressArray;
      } else {
        console.log('No valid address found.');
        return [];
      }
    }
  } catch (error) {
    console.error('Geocoding Error:', error);
    return [];
  }
};

export const jsonData = [
  {
    metricName: 'DeadClickCount',
    information: [
      {
        sessionsCount: '7',
        sessionsWithMetricPercentage: 42.86,
        sessionsWithoutMetricPercentage: 57.14,
        pagesViews: '6',
        subTotal: '9'
      }
    ]
  },
  {
    metricName: 'ExcessiveScroll',
    information: [
      {
        sessionsCount: '7',
        sessionsWithMetricPercentage: 0,
        sessionsWithoutMetricPercentage: 100,
        pagesViews: '0',
        subTotal: '0'
      }
    ]
  },
  {
    metricName: 'RageClickCount',
    information: [
      {
        sessionsCount: '7',
        sessionsWithMetricPercentage: 0,
        sessionsWithoutMetricPercentage: 100,
        pagesViews: '0',
        subTotal: '0'
      }
    ]
  },
  {
    metricName: 'QuickbackClick',
    information: [
      {
        sessionsCount: '7',
        sessionsWithMetricPercentage: 14.29,
        sessionsWithoutMetricPercentage: 85.71000000000001,
        pagesViews: '2',
        subTotal: '2'
      }
    ]
  },
  {
    metricName: 'ScriptErrorCount',
    information: [
      {
        sessionsCount: '7',
        sessionsWithMetricPercentage: 0,
        sessionsWithoutMetricPercentage: 100,
        pagesViews: '0',
        subTotal: '0'
      }
    ]
  },
  {
    metricName: 'ErrorClickCount',
    information: [
      {
        sessionsCount: '7',
        sessionsWithMetricPercentage: 0,
        sessionsWithoutMetricPercentage: 100,
        pagesViews: '0',
        subTotal: '0'
      }
    ]
  },
  {
    metricName: 'ScrollDepth',
    information: [
      {
        averageScrollDepth: 100
      }
    ]
  },
  {
    metricName: 'Traffic',
    information: [
      {
        totalSessionCount: '7',
        totalBotSessionCount: '2',
        distinctUserCount: '7',
        pagesPerSessionPercentage: 10.6
      }
    ]
  },
  {
    metricName: 'EngagementTime',
    information: [
      {
        totalTime: '494',
        activeTime: '177'
      }
    ]
  },
  {
    metricName: 'Browser',
    information: [
      {
        name: 'Chrome',
        sessionsCount: '5'
      },
      {
        name: 'Edge',
        sessionsCount: '1'
      },
      {
        name: 'Firefox',
        sessionsCount: '1'
      }
    ]
  },
  {
    metricName: 'Device',
    information: [
      {
        name: 'PC',
        sessionsCount: '7'
      }
    ]
  },
  {
    metricName: 'OS',
    information: [
      {
        name: 'Windows',
        sessionsCount: '7'
      }
    ]
  },
  {
    metricName: 'Country',
    information: [
      {
        name: 'Pakistan',
        sessionsCount: '7'
      }
    ]
  },
  {
    metricName: 'PageTitle',
    information: [
      {
        name: 'Shipments | TMS Shipping',
        sessionsCount: '3'
      },
      {
        name: 'Sign In | TMS Shipping',
        sessionsCount: '3'
      },
      {
        name: 'Quick Quote | TMS',
        sessionsCount: '2'
      },
      {
        name: 'Shiptechpro',
        sessionsCount: '1'
      },
      {
        name: 'Locations | TMS Shipping',
        sessionsCount: '1'
      },
      {
        name: 'User Profile | TMS',
        sessionsCount: '1'
      },
      {
        name: 'Track Shipment | TMS Shipping',
        sessionsCount: '1'
      }
    ]
  },
  {
    metricName: 'ReferrerUrl',
    information: [
      {
        name: 'https://tms.dynamicwerx.com/manage-shipments',
        sessionsCount: '3'
      },
      {
        name: null,
        sessionsCount: '1'
      },
      {
        name: 'https://tms.dynamicwerx.com/agency-signin',
        sessionsCount: '1'
      },
      {
        name: 'https://tms.dynamicwerx.com/agency-locked-screen',
        sessionsCount: '1'
      },
      {
        name: 'http://192.168.100.194:3000/agency-signin',
        sessionsCount: '1'
      }
    ]
  },
  {
    metricName: 'PopularPages',
    information: [
      {
        url: 'https://tms.dynamicwerx.com/quick-quote',
        visitsCount: '4'
      },
      {
        url: 'https://tms.dynamicwerx.com/manage-shipments',
        visitsCount: '4'
      },
      {
        url: 'https://tms.dynamicwerx.com/agency-signin',
        visitsCount: '4'
      },
      {
        url: 'https://tms.dynamicwerx.com/bookshipment',
        visitsCount: '3'
      },
      {
        url: 'http://localhost:3000/manage-shipments',
        visitsCount: '2'
      },
      {
        url: 'http://localhost:3000/track-shipment',
        visitsCount: '2'
      },
      {
        url: 'http://localhost:3000/agency-signin',
        visitsCount: '2'
      },
      {
        url: 'http://192.168.100.194:3000/agency-signin',
        visitsCount: '1'
      },
      {
        url: 'https://tms.dynamicwerx.com/carriers',
        visitsCount: '1'
      },
      {
        url: 'https://tms.dynamicwerx.com/add-locations',
        visitsCount: '1'
      }
    ]
  }
];
